import { useEffect, useState } from 'preact/hooks';
import { api } from '../api.js';
import { household, goBack, showToast, friendlyError, loadHousehold } from '../store.js';
import { __ } from '../i18n.js';
import Header from '../components/Header.jsx';
import BackButton from '../components/BackButton.jsx';
import Icon from '../components/Icon.jsx';
import { formatRelativeTime } from '../lib/format.js';

// star_shine is Material Symbols' current name for the sparkle glyph
// (formerly auto_awesome).
const ACTIVITY_ICONS = { completed: 'celebration', created: 'star_shine' };

/**
 * Phase 6 (roadmap §8): recent shared-task activity ("Anna voltooide
 * *Ramen lappen*"). Fetched once on mount — this screen already only
 * renders once a household exists, so there's no need for a global signal
 * the way `household` itself is one (nothing else reads this data).
 */
function ActivityFeed() {
  const [activity, setActivity] = useState(null);

  useEffect(() => {
    api.household
      .activity()
      .then((res) => setActivity(res.activity || []))
      .catch(() => setActivity([])); // purely additive — fail quiet, no error toast
  }, []);

  if (activity === null) {
    return null;
  }
  if (activity.length === 0) {
    return <p class="etm-field-hint">{__('Nog geen activiteit in dit huishouden.', 'etm')}</p>;
  }

  const now = Date.now();
  return (
    <div class="etm-task-list">
      {activity.map((item) => (
        <div class="etm-task-list-item" key={item.id}>
          <div class="etm-task-list-item__body">
            <div class="etm-task-list-item__title">
              <Icon name={item.type === 'completed' ? ACTIVITY_ICONS.completed : ACTIVITY_ICONS.created} size={16} />{' '}
              {item.type === 'completed'
                ? __('%s voltooide %s', 'etm').replace('%s', item.actor_name).replace('%s', item.task_title)
                : __('%s maakte %s aan', 'etm').replace('%s', item.actor_name).replace('%s', item.task_title)}
            </div>
            <div class="etm-task-list-item__meta">{formatRelativeTime(item.created_at, now)}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * HH-1: the household roster ("Gezinsleden") — plan §3.2.
 *
 * Exists because the week overview (HH-6) shows items for children, who
 * are not app users, so "person" cannot be a wp_user_id. Colours are MD3
 * *role names*, never hex (CLAUDE.md/WP-1) — the same six the server
 * whitelists in ETM_People::COLOR_ROLES; anything else it would reject.
 * Kept local to this screen rather than lifted into lib/: HH-6 will be the
 * second consumer, and the WP-2 precedent is to lift a shared constant
 * when that second consumer actually appears, not before.
 *
 * Fetched on mount with local state, matching ActivityFeed above — no
 * global signal, because nothing outside this screen reads the roster yet.
 */
const COLOR_ROLES = ['primary', 'secondary', 'tertiary', 'success', 'warning', 'error'];

function RosterSection() {
  const [people, setPeople] = useState(null);
  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);

  async function reload() {
    const res = await api.people.list();
    setPeople(res.people || []);
  }

  useEffect(() => {
    reload().catch(() => setPeople([]));
  }, []);

  async function run(action, successMessage) {
    if (busy) return;
    setBusy(true);
    try {
      await action();
      await reload();
      if (successMessage) showToast(successMessage, 'success');
    } catch (err) {
      // Real errors here are worth surfacing — unlike the decorative
      // activity feed, a failed rename/delete leaves the user looking at
      // stale data and needing to know why.
      showToast(friendlyError(err), 'error');
    } finally {
      setBusy(false);
    }
  }

  function handleAdd(event) {
    event.preventDefault();
    const trimmed = newName.trim();
    if (!trimmed) return;
    run(async () => {
      await api.people.create({ name: trimmed });
      setNewName('');
    }, __('Gezinslid toegevoegd', 'etm'));
  }

  function handleRename(person, name) {
    const trimmed = name.trim();
    if (!trimmed || trimmed === person.name) return;
    run(() => api.people.update(person.id, { name: trimmed }));
  }

  function handleColor(person, colorRole) {
    if (colorRole === person.color_role) return;
    run(() => api.people.update(person.id, { color_role: colorRole }));
  }

  /**
   * Reindexes the whole roster to its new array order and PATCHes only the
   * rows whose position actually changed. Deliberately not a two-row swap
   * of existing sort_order values: rows seeded at different times can share
   * a sort_order (or arrive all-zero), and swapping equal numbers is a
   * no-op that looks like a broken button. Reindexing converges on a clean
   * 0…n-1 regardless of what was stored before.
   */
  function handleMove(index, delta) {
    const target = index + delta;
    if (!people || target < 0 || target >= people.length) return;

    const reordered = people.slice();
    const [moved] = reordered.splice(index, 1);
    reordered.splice(target, 0, moved);

    run(async () => {
      for (let i = 0; i < reordered.length; i++) {
        if (reordered[i].sort_order !== i) {
          await api.people.update(reordered[i].id, { sort_order: i });
        }
      }
    });
  }

  function handleDelete(person) {
    if (!confirm(__('Gezinslid verwijderen?', 'etm'))) return;
    run(async () => {
      await api.people.remove(person.id);
      setEditingId(null);
    }, __('Gezinslid verwijderd', 'etm'));
  }

  if (people === null) {
    return null;
  }

  return (
    <>
      <h3>{__('Gezinsleden', 'etm')}</h3>
      <p class="etm-field-hint">
        {__('Wie horen er bij dit huishouden? Ook kinderen zonder eigen account.', 'etm')}
      </p>

      {people.length === 0 ? (
        <p class="etm-field-hint">{__('Nog geen gezinsleden.', 'etm')}</p>
      ) : (
        <div class="etm-roster">
          {people.map((person, index) => (
            <div class="etm-roster-item" key={person.id}>
              <div class="etm-roster-item__row">
                <span class={`etm-person-dot etm-person-dot--${person.color_role}`} aria-hidden="true" />
                {editingId === person.id ? (
                  <input
                    type="text"
                    class="etm-form-input etm-roster-item__input"
                    value={person.name}
                    maxLength={80}
                    autoComplete="off"
                    aria-label={__('Naam gezinslid', 'etm')}
                    onBlur={(event) => handleRename(person, event.currentTarget.value)}
                  />
                ) : (
                  <span class="etm-roster-item__name">{person.name}</span>
                )}
                <button
                  type="button"
                  class="etm-icon-btn"
                  aria-label={`${__('Gezinslid bewerken', 'etm')}: ${person.name}`}
                  onClick={() => setEditingId(editingId === person.id ? null : person.id)}
                >
                  <Icon name={editingId === person.id ? 'check' : 'edit'} size={20} />
                </button>
              </div>

              {editingId === person.id ? (
                <div class="etm-roster-item__editor">
                  <div class="etm-person-colors" role="group" aria-label={__('Kleur', 'etm')}>
                    {COLOR_ROLES.map((role) => (
                      <button
                        key={role}
                        type="button"
                        class={`etm-person-swatch etm-person-swatch--${role}${person.color_role === role ? ' active' : ''}`}
                        aria-label={role}
                        aria-pressed={person.color_role === role}
                        onClick={() => handleColor(person, role)}
                      />
                    ))}
                  </div>
                  <div class="etm-roster-item__actions">
                    <button
                      type="button"
                      class="etm-icon-btn"
                      aria-label={__('Omhoog', 'etm')}
                      disabled={index === 0}
                      onClick={() => handleMove(index, -1)}
                    >
                      <Icon name="arrow_upward" size={20} />
                    </button>
                    <button
                      type="button"
                      class="etm-icon-btn"
                      aria-label={__('Omlaag', 'etm')}
                      disabled={index === people.length - 1}
                      onClick={() => handleMove(index, 1)}
                    >
                      <Icon name="arrow_downward" size={20} />
                    </button>
                    <button
                      type="button"
                      class="etm-icon-btn etm-icon-btn--danger"
                      aria-label={__('Verwijderen', 'etm')}
                      onClick={() => handleDelete(person)}
                    >
                      <Icon name="delete" size={20} />
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          ))}
        </div>
      )}

      <form onSubmit={handleAdd} class="etm-roster-add">
        <input
          type="text"
          class="etm-form-input"
          value={newName}
          onInput={(event) => setNewName(event.currentTarget.value)}
          placeholder={__('Naam gezinslid', 'etm')}
          maxLength={80}
          autoComplete="off"
          aria-label={__('Naam gezinslid', 'etm')}
        />
        <button type="submit" class="etm-btn etm-btn-secondary etm-btn-sm" disabled={busy || !newName.trim()}>
          <Icon name="add" size={20} /> {__('Toevoegen', 'etm')}
        </button>
      </form>
    </>
  );
}

export default function HouseholdScreen() {
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [creating, setCreating] = useState(false);
  const [joining, setJoining] = useState(false);

  async function handleCopyCode() {
    try {
      await navigator.clipboard.writeText(household.value.invite_code);
      showToast(__('Code gekopieerd', 'etm'), 'success');
    } catch (err) {
      showToast(friendlyError(err), 'error');
    }
  }

  async function handleCreate(event) {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed || creating) return;
    setCreating(true);
    try {
      await api.household.create({ name: trimmed });
      await loadHousehold();
      showToast(__('Huishouden aangemaakt', 'etm'), 'success');
      setName('');
    } catch (err) {
      showToast(friendlyError(err), 'error');
    } finally {
      setCreating(false);
    }
  }

  async function handleJoin(event) {
    event.preventDefault();
    const trimmed = code.trim();
    if (!trimmed || joining) return;
    setJoining(true);
    try {
      await api.household.join({ code: trimmed });
      await loadHousehold();
      showToast(__('Je bent lid geworden van het huishouden', 'etm'), 'success');
      setCode('');
    } catch (err) {
      showToast(friendlyError(err), 'error');
    } finally {
      setJoining(false);
    }
  }

  return (
    <>
      <BackButton onClick={goBack} label={__('Instellingen', 'etm')} />
      <Header title={__('Huishouden', 'etm')} />

      {household.value ? (
        <>
          <div class="etm-household-card">
            <h2 class="etm-household-name">{household.value.name}</h2>
            <div class="etm-household-code">{household.value.invite_code}</div>
            <div class="etm-household-label">
              {__('Uitnodigingscode — deel deze met huisgenoten', 'etm')}
            </div>
            <button
              type="button"
              class="etm-btn etm-btn-secondary etm-btn-sm"
              onClick={handleCopyCode}
            >
              {__('Kopieer code', 'etm')}
            </button>
          </div>

          <RosterSection />

          <h3>{__('Activiteit', 'etm')}</h3>
          <ActivityFeed />
        </>
      ) : (
        <>
          <form onSubmit={handleCreate}>
            <h3>{__('Nieuw huishouden starten', 'etm')}</h3>
            <div class="etm-form-group">
              <label class="etm-form-label" htmlFor="household-name">
                {__('Naam van het huishouden', 'etm')}
              </label>
              <input
                id="household-name"
                type="text"
                class="etm-form-input"
                value={name}
                onInput={(event) => setName(event.currentTarget.value)}
                placeholder={__('Bijv. Familie Jansen', 'etm')}
                maxLength={100}
                autoComplete="off"
              />
            </div>
            <button
              type="submit"
              class="etm-btn etm-btn-primary"
              disabled={creating || !name.trim()}
            >
              {creating ? __('Bezig…', 'etm') : __('Huishouden starten', 'etm')}
            </button>
          </form>

          <form onSubmit={handleJoin}>
            <h3>{__('Lid worden van een huishouden', 'etm')}</h3>
            <div class="etm-form-group">
              <label class="etm-form-label" htmlFor="household-code">
                {__('Uitnodigingscode', 'etm')}
              </label>
              <input
                id="household-code"
                type="text"
                class="etm-form-input"
                value={code}
                onInput={(event) => setCode(event.currentTarget.value.toUpperCase())}
                placeholder={__('Voer code in', 'etm')}
                maxLength={20}
                autoComplete="off"
              />
            </div>
            <button
              type="submit"
              class="etm-btn etm-btn-secondary"
              disabled={joining || !code.trim()}
            >
              {joining ? __('Bezig…', 'etm') : __('Lid worden', 'etm')}
            </button>
          </form>
        </>
      )}
    </>
  );
}
